/**
 * Business Owner Reviews Management Component
 * 
 * Allows business owners to:
 * - View all reviews for their business
 * - Respond to reviews
 * - Track review stats and sentiment
 * - Filter unresponded reviews
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faReply,
  faFilter,
  faSort,
  faChartLine,
  faExclamationCircle,
  faSpinner,
  faEdit,
  faTrash,
  faCommentDots,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/CombinedAuthContext';
import { useToast } from './ui';
import { StarRating, ResponseForm } from './Reviews';
import '../styles/BusinessOwnerReviews.css';

const functions = getFunctions(undefined, 'us-central1');

/**
 * Review Stats Dashboard
 */
function ReviewStatsDashboard({ stats }) {
  if (!stats) return null;

  const { totalReviews, averageRating, responseRate, recentTrend, respondedCount } = stats;
  const unrespondedCount = totalReviews - respondedCount;

  return (
    <div className="review-stats-dashboard">
      <div className="stat-card main">
        <div className="stat-value">{averageRating?.toFixed(1) || '-'}</div>
        <div className="stat-label">Average Rating</div>
        <StarRating rating={averageRating || 0} size="small" />
      </div>

      <div className="stat-card">
        <div className="stat-value">{totalReviews}</div>
        <div className="stat-label">Total Reviews</div>
      </div>

      <div className="stat-card">
        <div className="stat-value">{responseRate}%</div>
        <div className="stat-label">Response Rate</div>
      </div>

      <div className="stat-card highlight">
        <div className="stat-value">{unrespondedCount}</div>
        <div className="stat-label">Need Response</div>
      </div>

      {recentTrend !== 'neutral' && (
        <div className={`trend-badge ${recentTrend}`}>
          <FontAwesomeIcon icon={faChartLine} />
          Trending {recentTrend}
        </div>
      )}
    </div>
  );
}

/**
 * Single Review Management Card
 */
function ReviewManagementCard({ 
  review, 
  onRespond, 
  onEditResponse, 
  onDeleteResponse,
  isResponding,
  isEditingResponse
}) {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={`review-management-card ${!review.response ? 'unresponded' : ''}`}>
      <div className="card-header">
        <div className="reviewer-info">
          <span className="reviewer-name">{review.userDisplayName}</span>
          <span className="review-date">{formatDate(review.createdAt)}</span>
        </div>
        <div className="review-rating">
          <StarRating rating={review.rating} size="small" showValue />
        </div>
      </div>

      {review.title && (
        <h4 className="review-title">{review.title}</h4>
      )}

      <p className="review-content">{review.content}</p>

      {review.visitDate && (
        <div className="visit-info">
          <FontAwesomeIcon icon={faCalendarAlt} />
          <span>Visited {formatDate(review.visitDate)}</span>
        </div>
      )}

      <div className="review-meta">
        <span className="helpful-count">
          {review.helpful || 0} found helpful
        </span>
        {review.reportCount > 0 && (
          <span className="report-badge">
            <FontAwesomeIcon icon={faExclamationCircle} />
            {review.reportCount} report{review.reportCount > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Existing Response */}
      {review.response && !isEditingResponse && (
        <div className="existing-response">
          <div className="response-header">
            <FontAwesomeIcon icon={faReply} />
            <span>Your Response</span>
            <span className="response-date">
              {formatDate(review.response.respondedAt)}
              {review.response.editedAt && ' (edited)'}
            </span>
          </div>
          <p className="response-text">{review.response.text}</p>
          <div className="response-actions">
            <button 
              className="action-btn"
              onClick={() => onEditResponse(review.id)}
            >
              <FontAwesomeIcon icon={faEdit} /> Edit
            </button>
            <button 
              className="action-btn danger"
              onClick={() => onDeleteResponse(review.id)}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </button>
          </div>
        </div>
      )}

      {/* Response Form (new or editing) */}
      {(isResponding || isEditingResponse) && (
        <div className="response-form-wrapper">
          <ResponseForm
            reviewId={review.id}
            existingResponse={isEditingResponse ? review.response : null}
            onSubmit={(text) => isEditingResponse 
              ? onEditResponse(review.id, text, true) 
              : onRespond(review.id, text)
            }
            onCancel={() => isEditingResponse 
              ? onEditResponse(review.id, null, false) 
              : onRespond(review.id, null)
            }
          />
        </div>
      )}

      {/* Respond Button */}
      {!review.response && !isResponding && (
        <button 
          className="respond-btn"
          onClick={() => onRespond(review.id)}
        >
          <FontAwesomeIcon icon={faCommentDots} />
          Write Response
        </button>
      )}
    </div>
  );
}

/**
 * Main Business Owner Reviews Component
 */
export function BusinessOwnerReviews({ businessId, businessName }) {
  // Auth context available if needed for future features
  useAuth();
  const { showToast } = useToast();

  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastReviewId, setLastReviewId] = useState(null);
  
  // Filters
  const [sortBy, setSortBy] = useState('recent');
  const [filterBy, setFilterBy] = useState('all'); // all, unresponded, responded, low-rated
  const [respondingTo, setRespondingTo] = useState(null);
  const [editingResponse, setEditingResponse] = useState(null);

  // Fetch reviews
  const fetchReviews = useCallback(async (append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const getBusinessReviews = httpsCallable(functions, 'getBusinessReviews');
      const result = await getBusinessReviews({
        businessId,
        sortBy,
        limit: 20,
        lastReviewId: append ? lastReviewId : null
      });

      if (result.data.success) {
        let newReviews = result.data.reviews;
        
        // Apply client-side filters
        if (filterBy === 'unresponded') {
          newReviews = newReviews.filter(r => !r.response);
        } else if (filterBy === 'responded') {
          newReviews = newReviews.filter(r => r.response);
        } else if (filterBy === 'low-rated') {
          newReviews = newReviews.filter(r => r.rating <= 2);
        }
        
        setReviews(prev => append ? [...prev, ...newReviews] : newReviews);
        setHasMore(result.data.hasMore);
        setLastReviewId(result.data.lastReviewId);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showToast('Failed to load reviews', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [businessId, sortBy, filterBy, lastReviewId, showToast]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const getReviewStats = httpsCallable(functions, 'getReviewStats');
      const result = await getReviewStats({ businessId });
      if (result.data.success) {
        setStats(result.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [businessId]);

  // Initial load
  useEffect(() => {
    fetchReviews();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, sortBy, filterBy]);

  // Handle respond to review
  const handleRespond = async (reviewId, text) => {
    if (text === null) {
      // Cancel
      setRespondingTo(null);
      return;
    }
    
    if (!text) {
      // Start responding
      setRespondingTo(reviewId);
      return;
    }
    
    try {
      const respondToReview = httpsCallable(functions, 'respondToReview');
      await respondToReview({ reviewId, responseText: text });
      showToast('Response posted successfully!', 'success');
      setRespondingTo(null);
      fetchReviews();
      fetchStats();
    } catch (error) {
      showToast(error.message || 'Failed to post response', 'error');
    }
  };

  // Handle edit response
  const handleEditResponse = async (reviewId, text, submit = false) => {
    if (!submit && text === null) {
      // Cancel editing
      setEditingResponse(null);
      return;
    }
    
    if (!submit) {
      // Start editing
      setEditingResponse(reviewId);
      return;
    }
    
    try {
      const editResponse = httpsCallable(functions, 'editResponse');
      await editResponse({ reviewId, responseText: text });
      showToast('Response updated!', 'success');
      setEditingResponse(null);
      fetchReviews();
    } catch (error) {
      showToast(error.message || 'Failed to update response', 'error');
    }
  };

  // Handle delete response
  const handleDeleteResponse = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this response?')) return;
    
    try {
      const deleteResponse = httpsCallable(functions, 'deleteResponse');
      await deleteResponse({ reviewId });
      showToast('Response deleted', 'success');
      fetchReviews();
      fetchStats();
    } catch (error) {
      showToast(error.message || 'Failed to delete response', 'error');
    }
  };

  // Filter counts
  const filterCounts = useMemo(() => {
    if (!stats) return { all: 0, unresponded: 0, responded: 0, lowRated: 0 };
    return {
      all: stats.totalReviews,
      unresponded: stats.totalReviews - stats.respondedCount,
      responded: stats.respondedCount,
      lowRated: (stats.ratingDistribution?.[1] || 0) + (stats.ratingDistribution?.[2] || 0)
    };
  }, [stats]);

  if (loading) {
    return (
      <div className="business-owner-reviews loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="business-owner-reviews">
      <div className="page-header">
        <h2>Reviews for {businessName}</h2>
        <p className="subtitle">Manage and respond to customer reviews</p>
      </div>

      {/* Stats Dashboard */}
      <ReviewStatsDashboard stats={stats} />

      {/* Filters & Sort */}
      <div className="filters-bar">
        <div className="filter-group">
          <FontAwesomeIcon icon={faFilter} />
          <select 
            value={filterBy} 
            onChange={e => setFilterBy(e.target.value)}
          >
            <option value="all">All Reviews ({filterCounts.all})</option>
            <option value="unresponded">
              Need Response ({filterCounts.unresponded})
            </option>
            <option value="responded">
              Responded ({filterCounts.responded})
            </option>
            <option value="low-rated">
              Low Rated ({filterCounts.lowRated})
            </option>
          </select>
        </div>

        <div className="filter-group">
          <FontAwesomeIcon icon={faSort} />
          <select 
            value={sortBy} 
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="recent">Most Recent</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
          </select>
        </div>
      </div>

      {/* Unresponded Alert */}
      {filterCounts.unresponded > 0 && filterBy === 'all' && (
        <div className="unresponded-alert">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>
            You have {filterCounts.unresponded} review{filterCounts.unresponded > 1 ? 's' : ''} waiting for a response
          </span>
          <button 
            className="view-btn"
            onClick={() => setFilterBy('unresponded')}
          >
            View
          </button>
        </div>
      )}

      {/* Reviews List */}
      <div className="reviews-list">
        {reviews.length === 0 ? (
          <div className="no-reviews">
            <p>
              {filterBy !== 'all' 
                ? 'No reviews match this filter'
                : 'No reviews yet'
              }
            </p>
          </div>
        ) : (
          reviews.map(review => (
            <ReviewManagementCard
              key={review.id}
              review={review}
              onRespond={handleRespond}
              onEditResponse={handleEditResponse}
              onDeleteResponse={handleDeleteResponse}
              isResponding={respondingTo === review.id}
              isEditingResponse={editingResponse === review.id}
            />
          ))
        )}
      </div>

      {/* Load More */}
      {hasMore && (
        <button 
          className="load-more-btn"
          onClick={() => fetchReviews(true)}
          disabled={loadingMore}
        >
          {loadingMore ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </>
          ) : (
            'Load More Reviews'
          )}
        </button>
      )}
    </div>
  );
}

export default BusinessOwnerReviews;
